import { BRIDGE_ERROR, BRIDGE_METHODS, BRIDGE_TIMEOUT_MS, BRIDGE_VERSION } from '../constants/bridge';
import { fail } from './result';
import { validateArgs, validateValue } from './schema';
import { runWithTimeout } from './timeout';

const TIMEOUTS = {
    [BRIDGE_METHODS.BIOMETRIC_AUTHENTICATE]: BRIDGE_TIMEOUT_MS.BIOMETRIC,
    [BRIDGE_METHODS.SHARE]: BRIDGE_TIMEOUT_MS.SHARE,
    [BRIDGE_METHODS.NOTIFICATION_REQUEST]: BRIDGE_TIMEOUT_MS.PERMISSION,
};

// Versioned facade shared by every host (browser, Capacitor, iframe). It
// validates input, enforces timeouts/cancellation and re-validates whatever
// the adapter returns, so an adapter bug cannot leak an unchecked value.
// Every method resolves to { ok: true, value } or { ok: false, error } and
// never throws.
export function createNativeBridge({ adapter }) {
    let isDisposed = false;
    const inflight = new Set();

    async function call(method, payload, { signal, timeoutMs } = {}) {
        if (isDisposed) return fail(BRIDGE_ERROR.DISPOSED);
        const args = validateArgs(method, payload);
        if (!args.valid) return fail(BRIDGE_ERROR.INVALID_ARGUMENT, args.reason);
        const handler = adapter.methods?.[method];
        if (typeof handler !== 'function') return fail(BRIDGE_ERROR.UNAVAILABLE);

        const controller = new AbortController();
        const handleAbort = () => controller.abort();
        if (signal?.aborted) controller.abort();
        else signal?.addEventListener('abort', handleAbort, { once: true });
        inflight.add(controller);

        try {
            const result = await runWithTimeout(
                ({ signal: innerSignal }) => handler(args.value, { signal: innerSignal }),
                { timeoutMs: timeoutMs ?? TIMEOUTS[method] ?? BRIDGE_TIMEOUT_MS.DEFAULT, signal: controller.signal },
            );
            if (isDisposed) return fail(BRIDGE_ERROR.DISPOSED);
            if (!result.ok) return result;
            const checked = validateValue(method, result.value);
            return checked.valid ? { ok: true, value: checked.value } : fail(BRIDGE_ERROR.INVALID_RESPONSE);
        } finally {
            signal?.removeEventListener('abort', handleAbort);
            inflight.delete(controller);
        }
    }

    return Object.freeze({
        version: BRIDGE_VERSION,
        host: adapter.host,
        getCapabilities: (options) => call(BRIDGE_METHODS.CAPABILITIES, {}, options),
        authenticateBiometric: ({ reason } = {}, options) => (
            call(BRIDGE_METHODS.BIOMETRIC_AUTHENTICATE, { reason }, options)
        ),
        getNotificationPermission: (options) => call(BRIDGE_METHODS.NOTIFICATION_PERMISSION, {}, options),
        requestNotificationPermission: (options) => call(BRIDGE_METHODS.NOTIFICATION_REQUEST, {}, options),
        notifyLocal: ({ title, body } = {}, options) => (
            call(BRIDGE_METHODS.NOTIFICATION_LOCAL, stripUndefined({ title, body }), options)
        ),
        share: ({ title, text, url } = {}, options) => (
            call(BRIDGE_METHODS.SHARE, stripUndefined({ title, text, url }), options)
        ),
        haptic: ({ style } = {}, options) => call(BRIDGE_METHODS.HAPTIC, { style }, options),
        dispose() {
            isDisposed = true;
            for (const controller of inflight) controller.abort();
            inflight.clear();
            adapter.dispose?.();
        },
    });
}

function stripUndefined(source) {
    return Object.fromEntries(Object.entries(source).filter(([, value]) => value !== undefined));
}
